(() => {
  class BrandVideoCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialised) {
        if (this.track) this.observer?.observe(this.track);
        return;
      }
      this.initialised = true;
      this.track = this.querySelector('[data-brand-video-track]');
      this.previous = this.querySelector('[data-brand-video-previous]');
      this.next = this.querySelector('[data-brand-video-next]');
      if (!this.track || !this.previous || !this.next) return;
      this.previous.addEventListener('click', () => this.move(-1));
      this.next.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      this.addEventListener('play', (event) => {
        if (!(event.target instanceof HTMLVideoElement)) return;
        this.querySelectorAll('video').forEach((video) => {
          if (video !== event.target) video.pause();
        });
      }, true);
      if (typeof ResizeObserver === 'function') {
        this.observer = new ResizeObserver(() => this.update());
        this.observer.observe(this.track);
      }
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    move(direction) {
      const first = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      const distance = first ? first.getBoundingClientRect().width + gap : this.track.clientWidth;
      this.track.scrollBy({
        left: direction * distance,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.previous.disabled = this.track.scrollLeft < 2;
      this.next.disabled = this.track.scrollLeft > max - 2;
      const controls = this.querySelector('[data-brand-video-controls]');
      if (controls) controls.hidden = max < 2;
    }
  }

  class BrandReviewList extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.select = this.querySelector('[data-brand-review-filter]');
      this.cards = [...this.querySelectorAll('[data-brand-review-age]')];
      this.empty = this.querySelector('[data-brand-review-empty]');
      this.status = this.querySelector('[data-brand-review-status]');
      if (!this.select) return;
      this.select.addEventListener('change', () => this.filter());
      this.querySelectorAll('[data-brand-review-clear]').forEach((button) => {
        button.addEventListener('click', () => {
          this.select.value = 'all';
          this.filter();
          this.select.focus();
        });
      });
    }

    filter() {
      let shown = 0;
      this.cards.forEach((card) => {
        card.hidden = this.select.value !== 'all' && card.dataset.brandReviewAge !== this.select.value;
        if (!card.hidden) shown += 1;
      });
      if (this.empty) this.empty.hidden = shown !== 0;
      if (this.status) {
        this.status.textContent = (this.dataset.resultText || '').replace('{count}', String(shown));
      }
    }
  }

  class BrandNewsletter extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.dialog = this.querySelector('dialog');
      this.surface = this.querySelector('[data-brand-newsletter-surface]');
      if (!this.dialog || !this.surface) return;
      if (typeof this.dialog.showModal !== 'function') {
        this.dialog.remove();
        this.querySelectorAll('[data-brand-newsletter-close], [data-brand-newsletter-preview]').forEach((button) => {
          button.hidden = true;
        });
        return;
      }
      this.dialog.append(this.surface);
      this.form = this.surface.querySelector('form');
      this.formState = this.surface.querySelector('[data-brand-form-state]')?.dataset.brandFormState;
      this.dismissKey = 'brand-newsletter-dismissed-v1';
      this.pendingKey = 'brand-newsletter-pending-v1';
      this.surface.querySelectorAll('[data-brand-newsletter-close]').forEach((button) => {
        button.addEventListener('click', () => this.dialog.close());
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
          this.dialog.close();
        }
      });
      this.dialog.addEventListener('close', () => {
        this.writeStorage(this.dismissKey, 'yes');
        if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      });
      this.form?.addEventListener('submit', () => this.writeStorage(this.pendingKey, this.id));
      this.querySelector('[data-brand-newsletter-preview]')?.addEventListener('click', () => this.open());
      this.sectionSelect = (event) => {
        if (this.closest('.shopify-section')?.id === `shopify-section-${event.detail.sectionId}`) this.open();
      };
      document.addEventListener('shopify:section:select', this.sectionSelect);
      const ownResponse = this.readStorage(this.pendingKey) === this.id || location.hash === `#${this.form?.id}`;
      if (ownResponse && ['success', 'error'].includes(this.formState)) {
        this.writeStorage(this.pendingKey, '');
        this.timer = window.setTimeout(() => this.tryOpen(), 0);
      } else if (this.formState === 'idle' && this.dataset.autoOpen === 'true' && !window.Shopify?.designMode && this.readStorage(this.dismissKey) !== 'yes') {
        const seconds = Math.min(15, Math.max(10, Number(this.dataset.delay) || 12));
        this.timer = window.setTimeout(() => this.tryOpen(), seconds * 1000);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      document.removeEventListener('shopify:section:select', this.sectionSelect);
      if (this.dialog?.open) this.dialog.close();
    }

    readStorage(key) {
      try {
        return sessionStorage.getItem(key);
      } catch {
        return this[key] || '';
      }
    }

    writeStorage(key, value) {
      this[key] = value;
      try {
        sessionStorage.setItem(key, value);
      } catch {
        return;
      }
    }

    otherModalOpen() {
      return [...document.querySelectorAll('dialog[open], [aria-modal="true"], cart-drawer.active, menu-drawer details[open], details-modal details[open]')]
        .some((node) => {
          if (node === this.dialog || this.contains(node) || node.getAttribute('aria-hidden') === 'true') return false;
          const style = getComputedStyle(node);
          if (style.visibility === 'hidden' || style.display === 'none') return false;
          const rect = node.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < innerHeight && rect.left < innerWidth;
        });
    }

    tryOpen() {
      if (!this.isConnected) return;
      if (this.formState === 'idle' && this.readStorage(this.dismissKey) === 'yes') return;
      if (document.hidden || this.otherModalOpen()) {
        this.timer = window.setTimeout(() => this.tryOpen(), 800);
        return;
      }
      this.open();
    }

    open() {
      if (this.dialog.open || typeof this.dialog.showModal !== 'function') return;
      window.clearTimeout(this.timer);
      this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.dialog.showModal();
      const response = this.surface.querySelector('[data-brand-form-response]');
      if (response) response.focus();
      else this.surface.querySelector('[data-brand-newsletter-close]')?.focus();
    }
  }

  if (!customElements.get('brand-video-carousel')) customElements.define('brand-video-carousel', BrandVideoCarousel);
  if (!customElements.get('brand-review-list')) customElements.define('brand-review-list', BrandReviewList);
  if (!customElements.get('brand-newsletter')) customElements.define('brand-newsletter', BrandNewsletter);
})();
